// The shared category/index axis. Renders the *real* recharts
// `<XAxis>` or `<YAxis>` (recharts v3 supports custom-component-wrapped
// axes) with a single stable id in both orientations, so the Tooltip
// can deterministically follow it. The model decides *which* physical
// axis is the category axis; this component only renders it.

import { XAxis, YAxis } from 'recharts';

import { AXIS_CHROME, bottomTitle, type LabelFormatter, sideTitle } from './axisLabels.tsx';
import type { ChartOrientation } from './chartOrientation.ts';

export function CategoryAxis({
  o,
  title,
  tickFormatter,
  ticks,
}: {
  o: ChartOrientation;
  title: string | undefined;
  tickFormatter: LabelFormatter;
  /**
   * Explicit tick positions on a continuous (number) category axis —
   * used to snap ticks to the data points themselves on sparse data
   * (recharts otherwise picks "nice" round numbers that miss the
   * actual values). Only forwarded when `o.categoryNumeric` is true;
   * ignored on string-category axes.
   */
  ticks?: readonly number[];
}) {
  const common = {
    ...AXIS_CHROME,
    dataKey: 'x',
    tickFormatter,
    type: (o.categoryNumeric ? 'number' : 'category') as 'number' | 'category',
    ...(o.categoryNumeric ? { domain: ['auto', 'auto'] as const } : {}),
    ...(o.categoryNumeric && ticks !== undefined ? { ticks: [...ticks] } : {}),
  };
  const stringCategory = !o.categoryNumeric;
  return o.categoryIsBottom ? (
    <XAxis
      {...common}
      {...(stringCategory ? {} : { scale: 'time' })}
      angle={-25}
      height={40}
      label={bottomTitle(title)}
      textAnchor="end"
      xAxisId={o.categoryAxisId}
    />
  ) : (
    <YAxis
      {...common}
      {...(stringCategory ? { interval: 0 } : {})}
      label={sideTitle(title, 'left')}
      width="auto"
      yAxisId={o.categoryAxisId}
    />
  );
}
